import Event from 'Faster/model/Event';
import TrackEventRequest from 'Faster/request/TrackEventRequest';
import DeviceStorage from 'Faster/storage/DeviceStorage';
import SessionStorage from 'Faster/storage/SessionStorage';
import FasterHttpClient from 'Faster/network/FasterHttpClient';
import FasterConfiguration from 'Faster/FasterConfiguration';

export default class EventService {
  private httpClient: FasterHttpClient;
  private deviceStorage: DeviceStorage;
  private sessionStorage: SessionStorage;
  private eventQueue: Event[] = [];
  private retryId: ReturnType<typeof setTimeout> = null;
  private retryTimeout: number;
  private retryCount = 0;
  private maxRetryIncrease: number;
  private isSendingEvents: boolean;

  public constructor(
    httpClient: FasterHttpClient,
    deviceStorage: DeviceStorage,
    sessionStorage: SessionStorage,
    configuration: FasterConfiguration
  ) {
    this.httpClient = httpClient;
    this.deviceStorage = deviceStorage;
    this.sessionStorage = sessionStorage;
    this.retryTimeout = configuration.eventRetryTimeout;
    this.maxRetryIncrease = configuration.eventRetryMax;
    this.isSendingEvents = false;
  }

  public sendEventWithPromise(event: Event): Promise<Response> {
    const deviceId = this.deviceStorage.deviceId;
    const session = this.sessionStorage.session;
    const request = new TrackEventRequest(deviceId, session.userId, session.id, [event]);

    return this.httpClient.execute(request);
  }

  public sendEvent(event: Event): void {
    if (!this.isEventValid(event)) {
      throw new Error('Invalid event');
    }

    this.eventQueue.push(event);

    this.sendQueuedEvents();
  }

  private isEventValid(event: Event): boolean {
    return event !== null && event !== undefined;
  }

  private sendQueuedEvents(): void {
    if (this.isSendingEvents) {
      return;
    }

    this.isSendingEvents = true;

    const deviceId = this.deviceStorage.deviceId;
    const session = this.sessionStorage.session;
    const eventsToSend = this.eventQueue;
    const request = new TrackEventRequest(deviceId, session.userId, session.id, eventsToSend);
    this.eventQueue = [];

    this.httpClient
      .execute(request)
      .then(() => {
        this.resetRetry();
        this.isSendingEvents = false;

        if (this.hasEventsToBeSent()) {
          this.sendQueuedEvents();
        }
      })
      .catch(() => {
        this.eventQueue = this.eventQueue.concat(eventsToSend);
        this.isSendingEvents = false;

        this.registerTimeout();
      });
  }

  private hasRegisteredRetry(): boolean {
    return this.retryId !== null;
  }

  private resetRetry(): void {
    clearTimeout(this.retryId);
    this.retryId = null;
    this.retryCount = 0;
  }

  private registerTimeout(): void {
    if (this.hasRegisteredRetry()) {
      return;
    }

    this.retryId = setTimeout(() => {
      this.retryId = null;
      this.retryEvents();
    }, this.retryTimeout * Math.pow(2, this.retryCount));
  }

  private hasEventsToBeSent(): boolean {
    return this.eventQueue.length > 0;
  }

  private retryEvents(): void {
    if (!this.hasEventsToBeSent()) {
      this.resetRetry();
      return;
    }

    if (this.retryCount < this.maxRetryIncrease) {
      this.retryCount++;
    }

    this.sendQueuedEvents();
  }
}
